/**
 * Admin Newsletter Tab - Manage newsletter subscribers and send campaigns
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Users, Send, AlertCircle, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useAdmin } from "./AdminContext";
import { API } from "../../lib/api";

const AdminNewsletterTab = () => {
  const { subscribers, newsletterStats, sentNewsletters, loadData } = useAdmin();
  
  const [showNewsletterForm, setShowNewsletterForm] = useState(false);
  const [newsletterForm, setNewsletterForm] = useState({ subject: "", content: "", test_mode: false });
  const [sendingNewsletter, setSendingNewsletter] = useState(false);

  const handleSendNewsletter = async () => {
    if (!newsletterForm.subject || !newsletterForm.content) {
      toast.error("Please fill in subject and content");
      return;
    }
    setSendingNewsletter(true);
    try {
      const res = await axios.post(`${API}/newsletter/send`, newsletterForm, { withCredentials: true });
      toast.success(res.data.message);
      setShowNewsletterForm(false);
      setNewsletterForm({ subject: "", content: "", test_mode: false });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send newsletter");
    } finally {
      setSendingNewsletter(false);
    }
  };

  const handleDeleteSubscriber = async (subscriberId) => {
    if (!confirm("Remove this subscriber?")) return;
    try {
      await axios.delete(`${API}/newsletter/subscriber/${subscriberId}`, { withCredentials: true });
      toast.success("Subscriber removed");
      loadData();
    } catch {
      toast.error("Failed to remove subscriber");
    }
  };

  return (
    <div className="grid gap-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Mail className="w-8 h-8 text-amber-600 mx-auto mb-2" />
            <p className="text-2xl font-bold">{newsletterStats?.active_subscribers || 0}</p>
            <p className="text-sm text-stone-500">Active Subscribers</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 text-stone-400 mx-auto mb-2" />
            <p className="text-2xl font-bold">{newsletterStats?.total_subscribers || 0}</p>
            <p className="text-sm text-stone-500">Total Subscribers</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Send className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold">{newsletterStats?.newsletters_sent || 0}</p>
            <p className="text-sm text-stone-500">Newsletters Sent</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-2xl font-bold">{newsletterStats?.unsubscribed || 0}</p>
            <p className="text-sm text-stone-500">Unsubscribed</p>
          </CardContent>
        </Card>
      </div>

      {/* Send Newsletter */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Send Newsletter</CardTitle>
            <CardDescription>Compose and send a newsletter to all active subscribers</CardDescription>
          </div>
          <Button onClick={() => setShowNewsletterForm(true)}>
            <Send className="w-4 h-4 mr-2" /> Compose Newsletter
          </Button>
        </CardHeader>
      </Card>

      {/* Recent Newsletters */}
      <Card>
        <CardHeader>
          <CardTitle>Sent Newsletters</CardTitle>
        </CardHeader>
        <CardContent>
          {sentNewsletters.length === 0 ? (
            <p className="text-center text-stone-500 py-8">No newsletters sent yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subject</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Failed</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sentNewsletters.map(nl => (
                  <TableRow key={nl.newsletter_id}>
                    <TableCell className="font-medium">{nl.subject}</TableCell>
                    <TableCell>{nl.recipient_count}</TableCell>
                    <TableCell className="text-green-600">{nl.sent_count}</TableCell>
                    <TableCell className="text-red-600">{nl.failed_count || 0}</TableCell>
                    <TableCell className="text-sm">{new Date(nl.sent_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Subscribers List */}
      <Card>
        <CardHeader>
          <CardTitle>Subscribers ({subscribers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {subscribers.length === 0 ? (
            <p className="text-center text-stone-500 py-8">No subscribers yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Subscribed</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscribers.slice(0, 50).map(sub => (
                  <TableRow key={sub.subscriber_id}>
                    <TableCell>{sub.email}</TableCell>
                    <TableCell>{sub.name}</TableCell>
                    <TableCell>
                      <Badge variant={sub.status === "active" ? "default" : "secondary"}>{sub.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{new Date(sub.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteSubscriber(sub.subscriber_id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Newsletter Compose Dialog */}
      <Dialog open={showNewsletterForm} onOpenChange={setShowNewsletterForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Compose Newsletter</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Subject *</Label>
              <Input 
                value={newsletterForm.subject}
                onChange={e => setNewsletterForm({...newsletterForm, subject: e.target.value})}
                placeholder="Newsletter subject line"
              />
            </div>
            <div>
              <Label>Content *</Label>
              <Textarea
                value={newsletterForm.content}
                onChange={(e) => setNewsletterForm({...newsletterForm, content: e.target.value})}
                className="bg-white min-h-[200px]"
                placeholder="Write your newsletter content here. HTML formatting is supported."
              />
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox"
                id="test_mode"
                checked={newsletterForm.test_mode}
                onChange={e => setNewsletterForm({...newsletterForm, test_mode: e.target.checked})}
              />
              <label htmlFor="test_mode" className="text-sm">Test mode (send only to admin email)</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewsletterForm(false)}>Cancel</Button>
            <Button 
              onClick={handleSendNewsletter} 
              disabled={sendingNewsletter}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {sendingNewsletter ? "Sending..." : (newsletterForm.test_mode ? "Send Test" : `Send to ${newsletterStats?.active_subscribers || 0} subscribers`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminNewsletterTab;
